import type React from 'react';
import { Badge } from '@localagent/ui/components/badge';
import type { OrgRole } from '../../types/orgchart';
import { ROLE_DISPLAY_NAMES } from '../../types/orgchart';
import type { TreeNode, NodeRenderProps } from '@localagent/ui/components/tree-view';

const ROLE_COLORS: Record<OrgRole, 'default' | 'info' | 'success' | 'warning' | 'secondary'> = {
  ceo: 'default',
  vp: 'info',
  director: 'success',
  manager: 'warning',
  ic: 'secondary',
};

interface PositionData {
  role: OrgRole;
  userId?: string | null;
  personaId?: string | null;
}

function OccupantIcon({ userId, personaId }: { userId?: string | null; personaId?: string | null }) {
  if (userId) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500">
        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    );
  }
  if (personaId) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-500">
        <rect width="18" height="10" x="3" y="11" rx="2" />
        <circle cx="12" cy="5" r="2" />
        <path d="M12 7v4" />
        <line x1="8" x2="8" y1="16" y2="16" />
        <line x1="16" x2="16" y1="16" y2="16" />
      </svg>
    );
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground/50">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12h8" />
    </svg>
  );
}

export function PositionNode({
  node,
  props,
}: {
  node: TreeNode;
  props: NodeRenderProps;
}) {
  const data = node.data as PositionData | undefined;
  const role = data?.role ?? 'ic';

  return (
    <div
      data-testid={`orgchart-node-${node.id}`}
      className={`group flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer transition-colors ${
        props.isSelected ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
      }`}
      onClick={props.onSelect}
    >
      {/* Expand/collapse for parent nodes */}
      {props.hasChildren ? (
        <button
          type="button"
          className="shrink-0 rounded-sm p-0.5 hover:bg-accent"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            props.onToggle();
          }}
          aria-label={props.isExpanded ? 'Collapse' : 'Expand'}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`shrink-0 transition-transform ${props.isExpanded ? 'rotate-90' : ''}`}
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      ) : (
        <span className="w-[22px] shrink-0" />
      )}

      {/* Occupant icon */}
      <OccupantIcon userId={data?.userId} personaId={data?.personaId} />

      {/* Title */}
      <span className="min-w-0 truncate text-sm font-medium">{node.label}</span>

      {/* Role badge */}
      <Badge variant={ROLE_COLORS[role]} className="ml-auto shrink-0 text-[10px]">
        {ROLE_DISPLAY_NAMES[role]}
      </Badge>

    </div>
  );
}
